import { useState, useEffect } from "react";
import { ref, get, set, push, remove, update, onValue } from "firebase/database";
import { database } from "@/config/firebase";
import { TattooImage } from "@/data/tattoos";

export type FirebaseTattoo = Omit<TattooImage, "id"> & { id?: string };

export const useTattoos = () => {
  const [tattoos, setTattoos] = useState<TattooImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Buscar todas as tatuagens
  useEffect(() => {
    console.log("Inicializando hook useTattoos");
    
    try {
      const tattoosRef = ref(database, "tattoos");
      console.log("Referência ao banco de dados criada:", tattoosRef.key);
      
      const unsubscribe = onValue(tattoosRef, (snapshot) => {
        setLoading(true);
        try {
          console.log("Snapshot recebido, existe:", snapshot.exists());
          
          if (snapshot.exists()) {
            const tattoosData = snapshot.val();
            console.log("Dados recebidos:", Object.keys(tattoosData).length, "itens");
            
            const tattoosArray = Object.entries(tattoosData).map(([id, data]) => ({
              id,
              ...(data as Omit<TattooImage, "id">),
            }));
            
            // Ordernar por campo 'order' se existir, caso contrário mantém a ordem padrão
            const sortedTattoos = tattoosArray.sort((a, b) => {
              if (a.order !== undefined && b.order !== undefined) {
                return a.order - b.order;
              }
              return 0;
            });
            
            setTattoos(sortedTattoos);
            console.log("Tatuagens ordenadas e atualizadas:", sortedTattoos.length);
          } else {
            console.log("Nenhum dado encontrado no snapshot");
            setTattoos([]);
          }
        } catch (error) {
          console.error("Erro ao processar dados:", error);
          setError(`Erro ao processar dados: ${(error as Error).message}`);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error("Erro no listener do Firebase:", error);
        setError(error.message);
        setLoading(false);
      });

      return () => {
        console.log("Desconectando listener do Firebase");
        unsubscribe();
      };
    } catch (error) {
      console.error("Erro ao criar referência ou listener:", error);
      setError(`Erro ao acessar banco de dados: ${(error as Error).message}`);
      setLoading(false);
      return () => {}; // Retorna uma função vazia para evitar erros
    }
  }, []);

  // Adicionar nova tatuagem
  const addTattoo = async (tattoo: FirebaseTattoo) => {
    try {
      setLoading(true);
      const tattoosRef = ref(database, "tattoos");
      
      // Adicionar timestamp de criação e definir ordem como 0 (primeira posição)
      const now = new Date().toISOString();
      const tattooToSave = {
        ...tattoo,
        createdAt: now,
        order: 0 // Nova tatuagem sempre vai para o topo da lista
      };
      
      // Criar atualizações em batch para reordenar tatuagens existentes
      const updates: Record<string, any> = {};
      
      // Primeiro, incrementar a ordem de todas as tatuagens existentes
      tattoos.forEach((existingTattoo) => {
        if (typeof existingTattoo.id === 'string') {
          const newOrder = (existingTattoo.order !== undefined ? existingTattoo.order : 0) + 1;
          updates[`tattoos/${existingTattoo.id}/order`] = newOrder;
        }
      });
      
      // Criar a nova tatuagem e obter sua chave
      const newTattooRef = push(tattoosRef);
      const newTattooKey = newTattooRef.key;
      
      if (newTattooKey) {
        // Adicionar a nova tatuagem no conjunto de atualizações
        updates[`tattoos/${newTattooKey}`] = tattooToSave;
        
        // Aplicar todas as atualizações de uma vez
        await update(ref(database), updates);
      }
      
      setLoading(false);
      return { success: true, id: newTattooKey };
    } catch (error) {
      setError((error as Error).message);
      setLoading(false);
      return { success: false, error: (error as Error).message };
    }
  };

  // Atualizar tatuagem existente
  const updateTattoo = async (id: string, tattoo: FirebaseTattoo) => {
    try {
      setLoading(true);
      const tattooRef = ref(database, `tattoos/${id}`);
      await update(tattooRef, tattoo);
      setLoading(false);
      return { success: true };
    } catch (error) {
      setError((error as Error).message);
      setLoading(false);
      return { success: false, error: (error as Error).message };
    }
  };

  // Atualizar a ordem das tatuagens
  const updateTattoosOrder = async (orderedIds: string[]) => {
    try {
      setLoading(true);
      
      // Criar batch de atualizações
      const updates: Record<string, number> = {};
      
      // Para cada id na ordem, definir o índice como ordem
      orderedIds.forEach((id, index) => {
        updates[`tattoos/${id}/order`] = index;
      });
      
      // Aplicar todas as atualizações de uma vez
      await update(ref(database), updates);
      
      setLoading(false);
      return { success: true };
    } catch (error) {
      setError((error as Error).message);
      setLoading(false);
      return { success: false, error: (error as Error).message };
    }
  };

  // Remover tatuagem
  const deleteTattoo = async (id: string) => {
    try {
      setLoading(true);
      const tattooRef = ref(database, `tattoos/${id}`);
      await remove(tattooRef);
      setLoading(false);
      return { success: true };
    } catch (error) {
      setError((error as Error).message);
      setLoading(false);
      return { success: false, error: (error as Error).message };
    }
  };

  // Buscar tatuagem específica por ID
  const getTattooById = async (id: string) => {
    try {
      const tattooRef = ref(database, `tattoos/${id}`);
      const snapshot = await get(tattooRef);
      
      if (snapshot.exists()) {
        return { 
          id, 
          ...snapshot.val() as Omit<TattooImage, "id">
        };
      } else {
        return null;
      }
    } catch (error) {
      setError((error as Error).message);
      return null;
    }
  };

  // Obter tatuagens recentes baseadas na data de criação
  const getRecentTattoos = (limit: number) => {
    return [...tattoos]
      .filter(tattoo => tattoo.createdAt)
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA; // Ordem decrescente (mais recentes primeiro)
      })
      .slice(0, limit);
  };

  return {
    tattoos,
    loading,
    error,
    addTattoo,
    updateTattoo,
    deleteTattoo,
    getTattooById,
    updateTattoosOrder,
    getRecentTattoos
  };
}; 